import Logger from '../helpers/Logger'
const expirationFormat = /^[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}$/
export const dataAttr = `data-`
export const deprecatedBlocklistAttr = `${dataAttr}blacklist-`
export const blocklistAttr = `${dataAttr}blocklist-`
export const expirationAttr = `data-expires-at`
export const PAGE_SETTINGS_SELECTOR = 'mediavine-settings'

/**
 * Handles parsing values off of the page lvl settings element
 * @class
 * Example Element:
 * <div
 *   id="mediavine-settings"
 *   data-blocklist-leaderboard="1"
 *   data-blocklist-content-desktop="1"
 *   data-expires-at="2018-04-02"
 *   data-content-desktop-spacing="2.0"
 *   data-content-mobile-spacing="5.0"
 * />
 */

export default class PageSettings {
  constructor(private _document: Document = document) {
    if (!this.element) {
      Logger.debug('Page Settings element is not present on page')
    }
  }

  get blocklist(): any {
    return this.attributesToObject(
      isBlocklistAttribute,
      deprecatedBlocklistAttr
    )
  }

  get settings(): object {
    return this.attributesToObject(isSettingsAttribute, dataAttr)
  }

  private attributesToObject(
    filter: (attr: Node) => boolean,
    substr: string
  ): object {
    const attributes =
      (this.element && Array.from(this.element.attributes)) || []
    const expiration = attributes.filter(isExpirationAttribute).pop()
    const isExpired = expiration && checkExpiration(expiration)
    if (!this.element || isExpired) {
      return {}
    }

    const settings = {}

    attributes.filter(filter).forEach((attr) => {
      settings[parseKey(attr, substr)] = attr.nodeValue
    })

    return settings
  }

  get element(): HTMLElement | null {
    return this._document.getElementById(PAGE_SETTINGS_SELECTOR)
  }
}

/** Returns true if expired */
export function checkExpiration(attribute: Node): boolean {
  if (!attribute) {
    return true
  }
  const expiresAt: string | null = attribute.nodeValue
  const hasCorrectFormat = expiresAt && expiresAt.match(expirationFormat)

  if (expiresAt && hasCorrectFormat) {
    return Date.parse(expiresAt) < new Date().getTime()
  }

  return true
}

export function parseKey(attribute: Node, substr: string): string {
  return attribute.nodeName.substr(substr.length).replace(/-/g, '_')
}

export function isExpirationAttribute(attr: Node): boolean {
  return attr.nodeName === expirationAttr
}

export function isDataAttribute(attr: Node): boolean {
  return attr.nodeName.substr(0, dataAttr.length) === dataAttr
}

export function isSettingsAttribute(attr: Node): boolean {
  return (
    isDataAttribute(attr) &&
    !isExpirationAttribute(attr) &&
    !isBlocklistAttribute(attr)
  )
}

export function isBlocklistAttribute(attr: Node): boolean {
  return (
    attr.nodeName.substr(0, deprecatedBlocklistAttr.length) ===
      deprecatedBlocklistAttr ||
    attr.nodeName.substr(0, blocklistAttr.length) === blocklistAttr
  )
}
